import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isMobile } from "@/utils/common";

const LEGACY_WOW_ANIMATIONS = [
  "fadeIn",
  "fadeInUp",
  "fadeInDown",
  "fadeInLeft",
  "fadeInRight",
  "fadeInUpBig",
  "fadeInDownBig",
  "fadeInLeftBig",
  "fadeInRightBig",
  "zoomIn",
  "zoomInUp",
  "zoomInDown",
  "zoomInLeft",
  "zoomInRight",
];

const SELECTOR = "[data-animate], .scroll-animate, .wow";

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function resolveAnimationName(element) {
  const dataAnimate = element.dataset.animate;
  if (dataAnimate) {
    return dataAnimate.replace(/^animate__/, "");
  }

  const legacyClass = LEGACY_WOW_ANIMATIONS.find((name) =>
    element.classList.contains(name)
  );
  return legacyClass || "fadeInUp";
}

function resolveDelay(element) {
  return element.dataset.animateDelay || element.dataset.wowDelay || "";
}

function resolveDuration(element, fallback) {
  return element.dataset.animateDuration || fallback;
}

function revealElement(element, defaultDuration) {
  const animation = resolveAnimationName(element);
  const delay = resolveDelay(element);
  const duration = resolveDuration(element, defaultDuration);

  element.classList.remove("scroll-animate--pending");

  if (delay) {
    element.style.setProperty("--animate-delay", delay);
  }
  element.style.animationDuration = duration;

  element.classList.add("animate__animated", `animate__${animation}`);
}

function bindScrollReveal() {
  if (prefersReducedMotion()) {
    document.querySelectorAll(SELECTOR).forEach((element) => {
      element.classList.remove("scroll-animate--pending");
    });
    return () => {};
  }

  const mobile = isMobile();
  const defaultDuration = mobile ? "0.65s" : "0.85s";
  const rootMargin = mobile ? "0px 0px -4% 0px" : "0px 0px -10% 0px";
  const threshold = mobile ? 0.06 : 0.1;

  const pendingElements = [];

  document.querySelectorAll(SELECTOR).forEach((element) => {
    if (element.dataset.scrollRevealBound === "true") return;
    element.dataset.scrollRevealBound = "true";
    element.classList.add("scroll-animate--pending");
    pendingElements.push(element);
  });

  if (!pendingElements.length) {
    return () => {};
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        revealElement(entry.target, defaultDuration);
        observer.unobserve(entry.target);
      });
    },
    { rootMargin, threshold }
  );

  pendingElements.forEach((element) => observer.observe(element));

  return () => {
    observer.disconnect();
    pendingElements.forEach((element) => {
      delete element.dataset.scrollRevealBound;
    });
  };
}

const useScrollReveal = () => {
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => bindScrollReveal(), 80);
    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);
};

export default useScrollReveal;
